import { createSelector } from 'reselect'
import { checkoutAccommodationOfferView, getAccommodationItems } from 'checkout/selectors/view/accommodation'
import qs from 'qs'
import { hasFlightSessionExpired } from 'components/Flights/FlightsSessionTimer/FlightsSessionTimer'
import { countOccupantsForFlights } from 'lib/offer/occupancyUtils'
import { getCharterFlightItems, getFlightItems, getNonCharterFlightItems } from './view/flights'
import { FlightsClassTypes, FlightSortOptions } from 'constants/flight'
import { nonNullable } from 'lib/array/arrayUtils'
import { FlightsFiltersPriceType, FlightsFiltersValues } from 'lib/flights/flightFilterTypes'

export const getCheckoutFlightFilters = createSelector(
  (state: App.State) => state.router.location.search,
  (search: string): FlightsFiltersValues => {
    const params = new URLSearchParams(search)

    return {
      originAirportCode: params.get('originAirportCode') || undefined,
      destinationAirportCode: params.get('destinationAirportCode') || undefined,
      departDate: params.get('departDate') || undefined,
      returnDate: params.get('returnDate') || undefined,
      fareClass: params.get('fareClass') as FlightsClassTypes || undefined,
      stops: params.getAll('stops'),
      stopDurations: params.get('stopDurations') || undefined,
      airlines: params.getAll('airlines'),
      cabinTypes: params.getAll('cabinTypes'),
      durations: params.get('durations') || undefined,
      minDepartureTime: params.get('minDepartureTime') || undefined,
      maxDepartureTime: params.get('maxDepartureTime') || undefined,
      minArrivalTime: params.get('minArrivalTime') || undefined,
      maxArrivalTime: params.get('maxArrivalTime') || undefined,
      recommended: params.getAll('recommended'),
      sort: params.get('sort') as FlightSortOptions ?? FlightSortOptions.Best,
      bags: params.getAll('bags'),
      stopOverCities: params.getAll('stopOverCities'),
      alliances: params.getAll('alliances'),
      carriers: [],
      priceFilterType: FlightsFiltersPriceType.ADULT,
    }
  },
)

export const getInflowFlightSearchParams = createSelector(
  (state: App.State) => getNonCharterFlightItems(state),
  (state: App.State) => getCharterFlightItems(state),
  (state: App.State) => checkoutAccommodationOfferView(state),
  (flightItems, charterFlightItems, offerView): string | undefined => {
    const offerViews = offerView.data

    if (charterFlightItems[0] && flightItems[0]) {
      return qs.stringify({
        departDate: charterFlightItems[0].flights.at(0)?.departingDate,
        returnDate: charterFlightItems[0].flights.at(-1)?.departingDate,
        originAirportCode: flightItems[0].originAirportCode,
        destinationAirportCode: flightItems[0].destinationAirportCode,
        fareClass: charterFlightItems[0].flights.at(0)?.fareFamily?.cabin?.toLowerCase()
      })
    }
    if (offerViews[0] && flightItems[0]) {
      return qs.stringify({
        departDate: offerViews[0].startDate,
        returnDate: offerViews[0].endDate,
        originAirportCode: flightItems[0].originAirportCode,
        destinationAirportCode: flightItems[0].destinationAirportCode,
      })
    }
  },
)

export const isFlightSessionExpired = createSelector(
  (state: App.State) => getFlightItems(state),
  (items) => {
    return items.length > 0 && hasFlightSessionExpired()
  },
)

export const isStandaloneFlight = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => getNonCharterFlightItems(state),
  (cartItems, flightItems): boolean => flightItems.length === 1 && cartItems.length === 1,
)

export const getCheckoutFlightOccupancies = createSelector(
  (state: App.State) => getAccommodationItems(state),
  (state: App.State) => getNonCharterFlightItems(state),
  (accommodationItems, flightItems): App.Occupants => {
    // If we have flight items, check if the first flight is bundled or standalone
    if (flightItems.length > 0) {
      const flightItem = flightItems[0]
      const isFlightBundled = (flightItem.bundledItemIds?.length ?? 0) > 0

      // For standalone flights (no bundledItemIds), use flight item occupancy directly
      if (!isFlightBundled) {
        return flightItem.occupants
      }
    }

    // For bundled flights or when no flight items exist, sum up accommodation occupancies
    const occupants = nonNullable(accommodationItems.map(item => (item as any).occupancy))
    return countOccupantsForFlights(occupants)
  },
)
